import { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  PageShell,
  Skeleton,
  toast,
  type DataTableColumn,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { ApiClientError } from '@/lib/http-core';
import { AdminNameCell } from '@/features/admin/components/AdminNameCell';
import { ExportTemplateColumnsEditor } from './ExportTemplateColumnsEditor';
import {
  ExportTemplatesNotReadyError,
  TEMPLATE_NAME_MAX,
  useOrderExportTemplateMutations,
  useOrderExportTemplatesQuery,
  type OrderExportTemplate,
} from './useOrderExportTemplates';

const formatDate = (iso: string | null): string => (iso ? iso.slice(0, 10) : '—');

interface Draft {
  templateId: string | null;
  name: string;
  columns: string[];
}

/**
 * QA-433 — 주문 엑셀 다운로드 양식 관리(고도몰 "다운로드 양식관리"). 양식명 + 항목(순서 포함)을 저장해 두고,
 * 주문 엑셀 다운로드 대화상자에서 양식을 골라 그 항목·순서로 내려받는다. 양식은 관리자 모두가 함께 쓴다.
 */
export const OrderExportTemplatesPage = () => {
  const { t } = useTranslation('orders');
  const query = useOrderExportTemplatesQuery();
  const { save, remove } = useOrderExportTemplateMutations();
  const [keyword, setKeyword] = useState('');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrderExportTemplate | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const rows = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    const list = query.data ?? [];
    return kw ? list.filter((r) => r.name.toLowerCase().includes(kw)) : list;
  }, [query.data, keyword]);

  const notReady = query.error instanceof ExportTemplatesNotReadyError;

  const submit = () => {
    if (!draft) return;
    const name = draft.name.trim();
    if (!name) return setFormError(t('orders.exportTemplates.errName', '양식명을 입력하세요.'));
    if (draft.columns.length === 0) return setFormError(t('orders.exportTemplates.errColumns', '다운로드 항목을 하나 이상 고르세요.'));
    setFormError(null);
    save.mutate(
      { templateId: draft.templateId, input: { name, columns: draft.columns } },
      {
        onSuccess: () => {
          toast.success(t('orders.exportTemplates.saved', '양식을 저장했습니다.'));
          setDraft(null);
        },
        onError: (e) =>
          setFormError(
            e instanceof ExportTemplatesNotReadyError
              ? t('orders.exportTemplates.notReady', '양식 저장은 서버(BE) 반영 후 사용할 수 있습니다.')
              : // BE 400(모르는 칸 키·이름 길이 등)은 사유(키 목록 포함)를 그대로 — 공통 문구로 바꾸면 무엇이 틀렸는지 모른다.
                e instanceof ApiClientError && e.status === 400 && e.message.trim()
                ? e.message
                : friendlyMessage(e, t('orders.exportTemplates.saveFail', '양식을 저장하지 못했습니다.')),
          ),
      },
    );
  };

  const columns: DataTableColumn<OrderExportTemplate>[] = [
    {
      key: 'no',
      header: 'No',
      align: 'center',
      width: 64,
      cell: (_r, i) => (
        <span className="tabular-nums text-xs" style={{ color: 'var(--content-muted)' }}>
          {rows.length - i}
        </span>
      ),
    },
    {
      key: 'name',
      header: t('orders.exportTemplates.col.name', '다운로드 양식명'),
      cell: (r) => (
        <button
          type="button"
          className="text-left text-[13px] font-medium hover:underline"
          style={{ color: 'var(--content-strong)' }}
          onClick={() => setDraft({ templateId: r.templateId, name: r.name, columns: [...r.columns] })}
          data-testid="tpl-name-link"
        >
          {r.name}
        </button>
      ),
    },
    {
      key: 'menu',
      header: t('orders.exportTemplates.col.menu', '메뉴 분류'),
      align: 'center',
      width: 110,
      cell: () => <span className="text-[12px]">{t('orders.exportTemplates.menuOrder', '주문')}</span>,
    },
    {
      key: 'count',
      header: t('orders.exportTemplates.col.count', '항목 수'),
      align: 'center',
      width: 90,
      cell: (r) => <span className="tabular-nums text-[12px]" data-testid="tpl-count">{r.columns.length}</span>,
    },
    {
      key: 'createdBy',
      header: t('orders.exportTemplates.col.createdBy', '등록자'),
      align: 'center',
      width: 140,
      cell: (r) => <AdminNameCell adminUserId={r.createdBy} />,
    },
    {
      key: 'createdAt',
      header: t('orders.exportTemplates.col.createdAt', '등록일'),
      align: 'center',
      width: 110,
      cell: (r) => <span className="tabular-nums text-[12px]">{formatDate(r.createdAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: 140,
      cell: (r) => (
        <div className="flex justify-end gap-1.5">
          <Button size="sm" variant="outline" onClick={() => setDraft({ templateId: r.templateId, name: r.name, columns: [...r.columns] })}>
            {t('orders.exportTemplates.edit', '수정')}
          </Button>
          <Button size="sm" variant="danger" onClick={() => setDeleteTarget(r)}>
            {t('orders.exportTemplates.delete', '삭제')}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <PageShell
      title={t('orders.exportTemplates.title', '엑셀 다운로드 양식')}
      description={t('orders.exportTemplates.description', '업무별로 주문 엑셀 항목·순서를 양식으로 저장해 두고, 주문 엑셀 다운로드에서 골라 씁니다. 양식은 관리자 모두가 함께 씁니다.')}
      actions={
        <Button size="sm" onClick={() => setDraft({ templateId: null, name: '', columns: [] })} disabled={notReady} data-testid="tpl-create">
          <Plus size={15} aria-hidden="true" />
          {t('orders.exportTemplates.create', '양식 등록')}
        </Button>
      }
      data-testid="order-export-templates-page"
    >
      <div className="mb-4 max-w-[360px]">
        <Input
          type="search"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder={t('orders.exportTemplates.searchPlaceholder', '양식명으로 검색')}
          aria-label={t('orders.exportTemplates.searchPlaceholder', '양식명으로 검색')}
        />
      </div>

      {query.isPending ? (
        <Skeleton className="h-[200px] w-full" />
      ) : notReady ? (
        <p className="rounded-md p-4 text-sm" style={{ background: 'var(--surface-1)', color: 'var(--content-muted)' }} data-testid="tpl-not-ready">
          {t('orders.exportTemplates.notReady', '양식 저장은 서버(BE) 반영 후 사용할 수 있습니다.')}
        </p>
      ) : query.isError ? (
        <EmptyState size="inline" title={friendlyMessage(query.error, t('orders.exportTemplates.loadFail', '양식 목록을 불러오지 못했습니다.'))} />
      ) : rows.length === 0 ? (
        <EmptyState size="inline" title={t('orders.exportTemplates.empty', '등록된 양식이 없습니다.')} />
      ) : (
        <DataTable columns={columns} rows={rows} rowKey={(r) => r.templateId} stickyHeader ariaLabel={t('orders.exportTemplates.title', '엑셀 다운로드 양식')} />
      )}

      <Dialog open={draft !== null} onOpenChange={(o) => { if (!o) { setDraft(null); setFormError(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>
              {draft?.templateId ? t('orders.exportTemplates.editTitle', '다운로드 양식 수정') : t('orders.exportTemplates.createTitle', '다운로드 양식 등록')}
            </DialogTitle>
            <DialogDescription>
              {t('orders.exportTemplates.editorHint', '왼쪽에서 항목을 눌러 고른 뒤 [+]로 옮기고, 오른쪽에서 순서를 바꿉니다. 오른쪽 순서가 엑셀 칸 순서입니다.')}
            </DialogDescription>
          </DialogHeader>
          {draft ? (
            <div className="flex flex-col gap-4">
              <label className="flex flex-col gap-1 text-sm">
                {t('orders.exportTemplates.col.name', '다운로드 양식명')}
                <div className="flex items-center gap-2">
                  <Input
                    value={draft.name}
                    maxLength={TEMPLATE_NAME_MAX}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className="max-w-[420px]"
                    data-testid="tpl-name"
                  />
                  <span className="text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                    {draft.name.length} / {TEMPLATE_NAME_MAX}
                  </span>
                </div>
              </label>
              <div className="flex gap-6 text-sm">
                <span style={{ color: 'var(--content-muted)' }}>{t('orders.exportTemplates.col.menu', '메뉴 분류')}</span>
                <span>{t('orders.exportTemplates.menuOrder', '주문')} · {t('orders.exportTemplates.locationOrderList', '주문 목록')}</span>
              </div>
              <ExportTemplateColumnsEditor value={draft.columns} onChange={(columns) => setDraft({ ...draft, columns })} />
              {formError ? (
                <p className="text-[12px]" style={{ color: 'var(--signal-danger)' }} role="alert" data-testid="tpl-error">
                  {formError}
                </p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('orders.actions.cancel', '취소')}</Button>
            </DialogClose>
            <Button size="sm" onClick={submit} disabled={save.isPending} data-testid="tpl-save">
              {save.isPending ? t('orders.exportTemplates.saving', '저장 중…') : t('orders.exportTemplates.save', '저장')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('orders.exportTemplates.deleteTitle', '양식 삭제')}</DialogTitle>
            <DialogDescription>
              {t('orders.exportTemplates.deleteConfirm', { defaultValue: '"{{name}}" 양식을 삭제할까요? 다른 관리자도 더 이상 쓸 수 없습니다.', name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">{t('orders.actions.cancel', '취소')}</Button>
            </DialogClose>
            <Button
              variant="danger"
              size="sm"
              disabled={remove.isPending}
              onClick={() =>
                deleteTarget &&
                remove.mutate(deleteTarget.templateId, {
                  onSuccess: () => {
                    toast.success(t('orders.exportTemplates.deleted', '양식을 삭제했습니다.'));
                    setDeleteTarget(null);
                  },
                  onError: (e) => toast.error(friendlyMessage(e, t('orders.exportTemplates.deleteFail', '양식을 삭제하지 못했습니다.'))),
                })
              }
            >
              {t('orders.exportTemplates.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
